import type { OnDestroy, OnInit} from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import type { Subscription} from 'rxjs';
import { first } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { HasErrorPipe } from '../../../../shared/pipes/has-error.pipe';
import { UserManagementService } from '../../../services/user-management.service';

@Component({
  selector: 'app-login',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, HasErrorPipe],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnDestroy, OnInit {
  private authService = inject(AuthService);
  private userService = inject(UserManagementService);
  private router = inject(Router);

  private unsubscribe: Subscription[] = [];

  constructor() {
    this.authService.amplifyGetUserSession().forEach((session: unknown) => {
      if (session && typeof session === 'object' && 'tokens' in session && session['tokens']) {
        this.authService.currentUserSubject.next(this.authService.getUserAuthDataMap(session));
        const tokens = session['tokens'] as { idToken?: { payload?: { identities?: { userId: string }[] } } };
        const userId = tokens?.idToken?.payload?.identities?.[0]?.userId;

        if (!userId) {
          console.error('User ID (PRID) not found in session tokens');
          this.router.navigate(['/access-denied']);
          return;
        }

        // Fetch user details by PRID and route based on role
        this.getUserDetailsAndRoute(userId);
      }
    });
  }

  /**
   * Fetch user details by PRID and route to appropriate dashboard based on role
   */
  private getUserDetailsAndRoute(prid: string): void {
    this.userService.getUserByPRID(prid).pipe(first()).subscribe({
      next: (user) => {
        // Update last login timestamp
        this.userService.updateLastLogin(prid).pipe(first()).subscribe({
          error: (err) => {
            console.error('Failed to update last login:', err);
            // Continue even if last login update fails
          }
        });

        if (!user) {
          // User authenticated via Azure AD but not in backend DB yet
          // Clear any stored user data
          this.authService.setUserData(null);
          // Route to dashboard anyway - dashboard will handle role extraction from auth session
          this.router.navigate(['/dashboard']);
          return;
        }

        // Store user data in AuthService so dashboard can access it
        this.authService.setUserData(user);

        // Use first role_name from system_role_info array if available
        const firstRole = user.system_role_info && user.system_role_info.length > 0 ? user.system_role_info[0].role_name : undefined;

        // Use role_name from system_role_info if available, else fallback to system_role
        let roleName = firstRole?.toLowerCase();
        if (!roleName && user.system_role) {
          roleName = user.system_role.toLowerCase();
        }

        if (roleName === 'system admin' || roleName === 'market admin' ||
            roleName === 'business unit admin' || roleName === 'bu admin' ||
            roleName === 'manager') {
          // All these roles go to dashboard
          this.router.navigate(['/dashboard']);
        } else {
          // Unknown or unauthorized role
          this.router.navigate(['/access-denied']);
        }
      },
      error: () => {
        // Clear any stored user data on error
        this.authService.setUserData(null);
        // Even on error, route to dashboard - let dashboard component handle the error
        this.router.navigate(['/dashboard']);
      }
    });
  }

  ngOnInit(): void {
    this.login('AzureAD');
  }

  login(provider: string): void {
  // ...existing code...
    const loginSubscr = this.authService
      .loginSSO(provider)
      .pipe(first())
      .subscribe();
    this.unsubscribe.push(loginSubscr);
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((obs) => obs.unsubscribe());
  }
}
